import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { connect } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import {
  TvAlert,
  TvButton,
  TvDiv,
  TvForm,
  TvFormGroup,
  TvH6,
  TvI,
  TvImg,
  TvInput,
  TvLabel,
  TvModalFoot,
  TvModalHead,
  TvModalMain,
  TvModalRoot,
  TvRadio,
  TvSmall,
  TvSpan,
} from "@trustvid/design-system";

import ProfileMenu from "../CommonForBoth/TopbarDropdown/ProfileMenu";
import logoLightSvg from "../../assets/images/logo-light.svg";

//i18n
import { withTranslation } from "react-i18next";

// Redux Store
import {
  showRightSidebarAction,
  toggleLeftmenu,
  changeSidebarType,
} from "../../store/actions";

import { CreateAccount, DeleteAccount, FetchAccountList, getSwitchAccount, invalidateAccountListCache } from "../../services/account";
import { toast } from 'react-toastify';
import { getUserData, persistUserData } from "../../utils/auth-helpers";
import { useAccountBrandingContext, useActiveAccountName } from "../../context/account-branding-context";
import DeleteModal from "../Common/DeleteModal";
import { buildScopedPath } from "../../utils/account-context";
import { isSaasSubscriptionChromePath } from "../../utils/saasSubscriptionChrome";
import { useSaasEntitlements } from "../../hooks/useSaasEntitlements";
import { ROUTE_PATHS, toScopedRoute } from "../../routes/path-map";
import { useCreateDesign } from "../../context/create-design-context";
import {
  resolveAccountDropdownAvatarUrl,
  resolveBrandedLogoDisplayUrls,
  ACCOUNT_BRANDING_UPDATED_EVENT,
} from "../../utils/account-branding-theme";

export const resolveCreatedAccountId = (createResponse) => {
  const normalizedIdCandidates = [
    createResponse?.newAccount?.id,
    createResponse?.newAccount?.dataValues?.id,
    createResponse?.account?.id,
    createResponse?.accountId,
    createResponse?.id,
  ];

  const matchedId = normalizedIdCandidates.find((candidate) => candidate !== undefined && candidate !== null && String(candidate).trim() !== "");
  if (!matchedId) {
    return null;
  }
  return String(matchedId);
};

export const upsertAccountInList = (accounts = [], nextAccount = null) => {
  if (!nextAccount?.id) {
    return accounts;
  }

  const normalizedId = String(nextAccount.id);
  const withoutCurrent = (accounts || []).filter((account) => String(account?.id) !== normalizedId);
  return [nextAccount, ...withoutCurrent];
};

export const removeAccountFromList = (accounts = [], accountId) => {
  if (!accountId) {
    return accounts;
  }
  return (accounts || []).filter((account) => String(account?.id) !== String(accountId));
};

export const isCompactAccountDropdownViewport = (viewportWidth) => Number(viewportWidth || 0) < 768;
export const getAccountDropdownMaxHeight = (viewportHeight) => {
  const normalizedHeight = Number(viewportHeight || 0);
  if (!normalizedHeight) {
    return 420;
  }

  return Math.max(260, Math.min(520, normalizedHeight - 120));
};

export const filterAccountsBySearch = (accounts = [], searchValue = "") => {
  const searchTerm = String(searchValue || "").trim().toLowerCase();
  if (!searchTerm) {
    return accounts || [];
  }
  return (accounts || []).filter((account) => {
    return (
      String(account?.name || "").toLowerCase().includes(searchTerm) ||
      String(account?.id || "").toLowerCase().includes(searchTerm)
    );
  });
};

/**
 * Typical SaaS users belong to one account — hide the switcher.
 * SuperAdmin / multi-account enterprise membership gets a dedicated dialog.
 */
export const shouldShowAccountSwitcherTrigger = ({
  allowAccountSwitch = false,
  isSuperAdmin = false,
  accountCount = 0,
} = {}) => {
  if (!allowAccountSwitch) {
    return false;
  }
  if (isSuperAdmin) {
    return true;
  }
  return Number(accountCount) > 1;
};

export const Header = props => {
  const [showAccountSwitcherModal, setShowAccountSwitcherModal] = useState(false);
  const [FetchAccountData, setFetchAccountData] = useState([]);
  const [accountSearch, setAccountSearch] = useState("");
  const [isFetchingAccounts, setIsFetchingAccounts] = useState(false);
  const [accountFetchError, setAccountFetchError] = useState("");
  const [switchingAccountId, setSwitchingAccountId] = useState(null);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [addAccountErrors, setAddAccountErrors] = useState({});
  const [isCompactDropdown, setIsCompactDropdown] = useState(() =>
    typeof window !== "undefined" ? isCompactAccountDropdownViewport(window.innerWidth) : false
  );
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(() =>
    typeof window !== "undefined" ? getAccountDropdownMaxHeight(window.innerHeight) : 420
  );
  const [addAccountForm, setAddAccountForm] = useState({
    name: "",
    is_active: true,
    logo: null,
  });
  const {
    activeAccount,
    switchAccount,
    registerAvailableAccounts,
    branding,
    allowAccountSwitch,
  } = useAccountBrandingContext();
  const activeAccountId = activeAccount?.id;
  const activeAccountName = useActiveAccountName();
  const location = useLocation();
  const { openCreateDesign } = useCreateDesign();
  const hideLeftSidebar =
    props.hideLeftSidebar === true || isSaasSubscriptionChromePath(location.pathname);
  const saasEntitlements = useSaasEntitlements(activeAccountId)?.entitlements || {};
  const isPremiumPlan = Boolean(saasEntitlements?.isPremium);
  const isEnterprisePlan = Boolean(saasEntitlements?.isEnterprise);
  let planBadgeLabel = "Free";
  if (isEnterprisePlan) {
    planBadgeLabel = "Enterprise";
  } else if (isPremiumPlan) {
    planBadgeLabel = "Pay as you go";
  }
  const creditsLabel = Number(saasEntitlements?.creditsAvailable || 0).toLocaleString("en-IN");
  const headerCreditPillText = `${creditsLabel} credits`;
  const { iconSrc } = resolveBrandedLogoDisplayUrls(branding);
  const defaultHeaderMark = require("../../assets/images/trustvid-logo.png");

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handleResize = () => {
      setIsCompactDropdown(isCompactAccountDropdownViewport(window.innerWidth));
      setDropdownMaxHeight(getAccountDropdownMaxHeight(window.innerHeight));
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function tToggle() {
    var body = document.body;
    if (window.screen.width <= 998) {
      body.classList.toggle("sidebar-enable");
    } else {
      body.classList.toggle("vertical-collpsed");
      body.classList.toggle("sidebar-enable");
    }

  }

  const fetchAccountDataList = useCallback(async () => {
    setAccountSearch("");
    setIsFetchingAccounts(true);
    setAccountFetchError("");
    try {
      const response = await FetchAccountList();
      if (response?.error) {
        setAccountFetchError(response.error);
        setFetchAccountData([]);
        return;
      }
      const accounts = response?.accounts || [];
      setFetchAccountData(accounts);
      registerAvailableAccounts(accounts);
    } catch (error) {
      console.error("Error fetching account data:", error);
      setAccountFetchError("Failed to load accounts.");
    } finally {
      setIsFetchingAccounts(false);
    }
  }, [registerAvailableAccounts]);

  useEffect(() => {
    // Keep account name in sync on direct URL navigation
    // without requiring the user to open the account dropdown first.
    if (!allowAccountSwitch) {
      return;
    }
    fetchAccountDataList();
  }, [allowAccountSwitch, fetchAccountDataList]);

  useEffect(() => {
    if (!allowAccountSwitch) {
      return undefined;
    }
    const refreshAccountsAfterBrandingSave = () => {
      fetchAccountDataList();
    };
    window.addEventListener(ACCOUNT_BRANDING_UPDATED_EVENT, refreshAccountsAfterBrandingSave);
    return () => {
      window.removeEventListener(ACCOUNT_BRANDING_UPDATED_EVENT, refreshAccountsAfterBrandingSave);
    };
  }, [allowAccountSwitch, fetchAccountDataList]);

  const resetAddAccountForm = () => {
    setAddAccountForm({
      name: "",
      is_active: true,
      logo: null,
    });
    setAddAccountErrors({});
  };

  const openAccountSwitcherModal = () => {
    setAccountSearch("");
    setShowAccountSwitcherModal(true);
    fetchAccountDataList();
  };

  const closeAccountSwitcherModal = () => {
    if (switchingAccountId) {
      return;
    }
    setShowAccountSwitcherModal(false);
    setAccountSearch("");
  };

  const openAddAccountModal = () => {
    resetAddAccountForm();
    setShowAccountSwitcherModal(false);
    setShowAddAccountModal(true);
  };

  const closeAddAccountModal = () => {
    if (creatingAccount) {
      return;
    }
    setShowAddAccountModal(false);
  };

  const validateAddAccountForm = () => {
    const errors = {};
    const trimmedName = String(addAccountForm.name || "").trim();
    if (!trimmedName) {
      errors.name = "Account name is required.";
    }
    if (addAccountForm.logo) {
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!allowedTypes.includes(addAccountForm.logo.type)) {
        errors.logo = "Logo must be jpg, png, gif, or webp.";
      }
    } else {
      errors.logo = "Logo is required.";
    }
    setAddAccountErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateAccountFromDialog = async (event) => {
    event.preventDefault();
    if (!validateAddAccountForm()) {
      return;
    }
    const trimmedName = String(addAccountForm.name || "").trim();
    setCreatingAccount(true);
    const payload = new FormData();
    payload.append("name", trimmedName);
    payload.append("is_active", addAccountForm.is_active ? "true" : "false");
    payload.append("logo", addAccountForm.logo);

    try {
      const createResponse = await CreateAccount(payload);
      const newAccountId = resolveCreatedAccountId(createResponse);
      if (createResponse?.error || !newAccountId) {
        toast.error(createResponse?.error || "Failed to create account.");
        setCreatingAccount(false);
        return;
      }

      const createdAccountCandidate =
        createResponse?.newAccount?.dataValues ||
        createResponse?.newAccount ||
        createResponse?.account ||
        {};
      const normalizedCreatedAccount = {
        id: newAccountId,
        name: createdAccountCandidate?.name || trimmedName,
        logo: createdAccountCandidate?.logo || "",
        host_slug: createdAccountCandidate?.host_slug ?? null,
        is_active:
          typeof createdAccountCandidate?.is_active === "boolean"
            ? createdAccountCandidate.is_active
            : addAccountForm.is_active,
      };

      invalidateAccountListCache();
      let accountsFromApi = [];
      try {
        const accountsResponse = await FetchAccountList();
        if (Array.isArray(accountsResponse?.accounts)) {
          accountsFromApi = accountsResponse.accounts;
        }
      } catch (fetchListError) {
        console.error("Error refreshing account list after create:", fetchListError);
      }

      const mergedAccounts = upsertAccountInList(accountsFromApi.length ? accountsFromApi : FetchAccountData, normalizedCreatedAccount);
      setFetchAccountData(mergedAccounts);
      registerAvailableAccounts(mergedAccounts);

      const switchResponse = await getSwitchAccount(newAccountId);
      if (switchResponse?.error) {
        toast.error(switchResponse.error);
        setCreatingAccount(false);
        return;
      }

      localStorage.setItem("token", switchResponse?.token);
      localStorage.setItem("accountId", String(newAccountId));
      persistUserData(switchResponse?.user);
      setShowAddAccountModal(false);
      resetAddAccountForm();
      switchAccount(newAccountId, {
        replace: true,
        preserveSearch: true,
        targetHostSlug: normalizedCreatedAccount.host_slug ?? null,
      });
      toast.success("Account created and switched successfully.");
    } catch (error) {
      console.error("Error creating account from dialog:", error);
      toast.error("Failed to create account.");
    }
    setCreatingAccount(false);
  };

  const handleDeleteAccountClick = (event, account) => {
    event.preventDefault();
    event.stopPropagation();
    if (!account?.id) {
      return;
    }
    if (String(account.id) === String(activeAccountId)) {
      toast.error("You cannot delete the currently active account.");
      return;
    }
    setAccountToDelete(account);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteAccountConfirm = async () => {
    const deleteId = accountToDelete?.id;
    if (!deleteId) {
      setIsDeleteModalOpen(false);
      return;
    }
    if (String(deleteId) === String(activeAccountId)) {
      toast.error("You cannot delete the currently active account.");
      setIsDeleteModalOpen(false);
      return;
    }

    setIsDeletingAccount(true);
    try {
      const response = await DeleteAccount(deleteId);
      if (response?.error) {
        toast.error(response.error);
        return;
      }

      const nextList = removeAccountFromList(FetchAccountData, deleteId);
      setFetchAccountData(nextList);
      registerAvailableAccounts(nextList);
      invalidateAccountListCache();
      await fetchAccountDataList()
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Failed to delete account.");
    } finally {
      setIsDeletingAccount(false);
      setIsDeleteModalOpen(false);
      setAccountToDelete(null);
    }
  };

  const handleOnSwitchAccount = async (id) => {
    if (!id || String(id) === String(activeAccountId)) {
      setShowAccountSwitcherModal(false);
      return;
    }
    setSwitchingAccountId(id);
    try {
      const response = await getSwitchAccount(id);
      if (response?.error) {
        toast.error(response.error);
      } else {
        localStorage.setItem("token", response?.token);
        localStorage.setItem("accountId", id);
        persistUserData(response?.user);
        const picked = FetchAccountData.find((a) => String(a.id) === String(id));
        const switchOpts = { replace: true, preserveSearch: true };
        if (picked && Object.prototype.hasOwnProperty.call(picked, "host_slug")) {
          switchOpts.targetHostSlug = picked.host_slug;
        }
        switchAccount(id, switchOpts);
        toast.success("Switched account successfully.");
        setShowAccountSwitcherModal(false);
      }
    } catch (err) {
      console.error("Error fetching account details:", err);
      toast.error("Account switch failed.");
    }
    setSwitchingAccountId(null);
  };

  const selectedAccount = getUserData();
  const isSuperAdmin = selectedAccount?.role === "SuperAdmin";
  const showAccountSwitcher = shouldShowAccountSwitcherTrigger({
    allowAccountSwitch,
    isSuperAdmin,
    accountCount: FetchAccountData.length,
  });

  const filteredAccounts = useMemo(
    () => filterAccountsBySearch(FetchAccountData, accountSearch),
    [FetchAccountData, accountSearch]
  );

  const accountTotalLabel = useMemo(() => {
    if (isFetchingAccounts) {
      return "Loading...";
    }
    const total = FetchAccountData.length;
    const searchTerm = accountSearch.trim();
    if (!searchTerm) {
      return `${total} total`;
    }
    return `${filteredAccounts.length} of ${total}`;
  }, [FetchAccountData.length, accountSearch, filteredAccounts.length, isFetchingAccounts]);

  const currentAccountPreview = useMemo(() => {
    return (
      FetchAccountData.find((account) => String(account?.id) === String(activeAccountId)) ||
      activeAccount ||
      {}
    );
  }, [FetchAccountData, activeAccountId, activeAccount]);

  const renderAccountAvatar = (account, size = 30) => {
    const accountName = String(account?.name || "A");
    const logoUrl = resolveAccountDropdownAvatarUrl(account, {
      activeAccountId,
      activeBranding: branding,
    });
    if (logoUrl) {
      return (
        <TvImg
          key={`${account?.id}-${logoUrl}`}
          src={logoUrl}
          alt={accountName}
          loading="lazy"
          width={size}
          height={size}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: "999px",
            objectFit: "cover",
            border: "1px solid var(--bs-border-color)",
            flexShrink: 0,
          }}
        />
      );
    }
    return (
      <TvDiv
        className="rounded-circle d-flex align-items-center justify-content-center"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          background: "var(--bs-tertiary-bg)",
          color: "var(--bs-heading-color)",
          fontWeight: 700,
          fontSize: size <= 28 ? "12px" : "14px",
          flexShrink: 0,
        }}
      >
        {accountName.charAt(0).toUpperCase()}
      </TvDiv>
    );
  };

  const logoTargetPath = activeAccountId ? buildScopedPath(activeAccountId, "/dashboard") : "/login";

  const accountMutedTextStyle = useMemo(
    () => ({ fontSize: "12px", color: "var(--bs-secondary-color)", lineHeight: 1.1 }),
    []
  );
  const accountNameTextStyle = useMemo(
    () => ({
      fontSize: "13px",
      fontWeight: 600,
      maxWidth: "180px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      lineHeight: 1.2,
      color: "var(--bs-body-color)",
    }),
    []
  );
  const accountListNameTextStyle = useMemo(
    () => ({
      fontSize: isCompactDropdown ? "12px" : "13px",
      fontWeight: 600,
      color: "var(--bs-body-color)",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    [isCompactDropdown]
  );
  const accountChipSurfaceStyle = useMemo(
    () => ({
      border: "1px solid var(--bs-border-color)",
      borderRadius: "10px",
      background: "var(--bs-secondary-bg)",
    }),
    []
  );

  return (
    <React.Fragment>
      <TvDiv id="page-topbar" role="banner">
        <TvDiv className="navbar-header">
          <TvDiv className="d-flex">

            <TvDiv className="navbar-brand-box d-lg-none d-md-block">
              <Link to={logoTargetPath} className="logo logo-dark" data-testid="top-left-logo-link">
                <TvSpan className="logo-sm">
                  <TvSpan>
                    <TvImg
                      src={iconSrc || defaultHeaderMark}
                      alt="logo"
                      style={{ height: "34px"}}
                    />
                  </TvSpan>
                </TvSpan>
              </Link>

              <Link to={logoTargetPath} className="logo logo-light">
                <TvSpan className="logo-sm">
                  <TvImg src={iconSrc || logoLightSvg} alt="" height="22" />
                </TvSpan>
              </Link>
            </TvDiv>

            {hideLeftSidebar ? null : (
            <TvButton
              type="button"
              color="link"
              onClick={() => {
                tToggle();
              }}
              className="btn btn-sm px-3 font-size-16 header-item"
              id="vertical-menu-btn"
              data-testid="vertical-menu-btn"
            >
              <TvI className="fa fa-fw fa-bars" />
            </TvButton>
            )}
            {showAccountSwitcher ? (
            <>
              <TvButton
                type="button"
                color="link"
                className="tv-saas-account-switcher header-item ms-2"
                onClick={openAccountSwitcherModal}
                data-testid="header-account-dropdown-toggle"
              >
                <TvDiv className="d-flex align-items-center text-start">
                  <TvDiv className="me-2">
                    {renderAccountAvatar(currentAccountPreview, 28)}
                  </TvDiv>
                  <TvDiv className="d-flex flex-column">
                    <TvSpan style={accountMutedTextStyle}>
                      {props.t("Change Account")}
                    </TvSpan>
                    <TvSpan style={accountNameTextStyle}>
                      {activeAccountName || "Select account"}
                    </TvSpan>
                  </TvDiv>
                </TvDiv>
              </TvButton>
              <TvModalRoot
                isOpen={showAccountSwitcherModal}
                toggle={closeAccountSwitcherModal}
                centered
                className="tv-saas-account-dialog"
              >
                <TvDiv
                  data-testid="header-account-dropdown-menu"
                  className="tv-saas-account-dialog__panel"
                  style={{
                    minWidth: isCompactDropdown ? "min(92vw, 320px)" : "420px",
                    maxWidth: isCompactDropdown ? "min(92vw, 360px)" : "460px",
                  }}
                >
                <TvDiv
                  data-testid="header-account-dropdown-content"
                  style={{
                    maxHeight: `${dropdownMaxHeight}px`,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                <TvDiv className={`${isCompactDropdown ? "p-2" : "p-3"} pb-2 border-bottom`} style={{ flexShrink: 0 }}>
                  <TvDiv className="d-flex align-items-center justify-content-between mb-2">
                    <TvH6 className="mb-0 fw-bold" style={{ fontSize: "15px" }}>
                      {props.t("Select an account")}
                    </TvH6>
                    <TvSpan className="text-muted" style={{ fontSize: "12px" }}>
                      {accountTotalLabel}
                    </TvSpan>
                  </TvDiv>
                  <TvDiv className="position-relative">
                    <i
                      className="fa fa-search"
                      style={{
                        position: "absolute",
                        left: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "var(--bs-secondary-color)",
                      }}
                    />
                    <TvInput
                      type="search"
                      className="form-control"
                      placeholder="Search by name or id"
                      value={accountSearch}
                      onChange={(event) => setAccountSearch(event.target.value)}
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      name="tv-header-account-search"
                      data-testid="header-account-search-input"
                      data-lpignore="true"
                      data-form-type="other"
                      style={{ paddingLeft: "34px", borderRadius: "10px" }}
                    />
                  </TvDiv>
                </TvDiv>

                <TvDiv
                  data-testid="header-account-list-scroll-area"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    padding: isCompactDropdown ? "6px 8px" : "8px 10px",
                  }}
                >
                  {accountFetchError && (
                    <TvAlert color="warning" className="py-2 mb-2">
                      {accountFetchError}
                    </TvAlert>
                  )}
                  {isFetchingAccounts && (
                    <TvDiv className="text-muted text-center py-3" style={{ fontSize: "13px" }}>
                      Loading accounts...
                    </TvDiv>
                  )}
                  {showAccountSwitcherModal && filteredAccounts.map((account, index) => {
                    const isActive = String(account.id) === String(activeAccountId);
                    const isSwitching = String(account.id) === String(switchingAccountId);
                    return (
                      <TvDiv
                        key={`${account.id}-${index}`}
                        className="d-flex align-items-center gap-2 mb-1"
                      >
                        <TvButton
                          type="button"
                          color="link"
                          data-testid={`header-account-option-${account.id}`}
                          className="w-100 text-start border-0 bg-transparent p-0 shadow-none text-body"
                          onClick={() => handleOnSwitchAccount(account.id)}
                          disabled={isSwitching}
                          style={{ cursor: "pointer" }}
                        >
                          <TvDiv
                            className={`tv-saas-account-option d-flex align-items-center justify-content-between px-2 py-2${isActive ? " is-active" : ""}`}
                          >
                            <TvDiv className="d-flex align-items-center" style={{ minWidth: 0 }}>
                              <TvDiv className="me-2">
                                {renderAccountAvatar(account, 30)}
                              </TvDiv>
                              <TvDiv style={{ minWidth: 0 }}>
                                <TvDiv className="fw-semibold" style={accountListNameTextStyle}>
                                  {account.name}
                                </TvDiv>
                              </TvDiv>
                            </TvDiv>
                            {isSwitching && <TvSpan className="text-muted small">Switching...</TvSpan>}
                            {!isSwitching && isActive && (
                              <TvSpan className="tv-saas-account-check" aria-hidden="true">
                                <TvI className="mdi mdi-check" />
                              </TvSpan>
                            )}
                          </TvDiv>
                        </TvButton>
                        {isSuperAdmin && (
                          <TvButton
                            type="button"
                            size="sm" color="danger" outline
                            data-testid={`header-account-delete-${account.id}`}
                            aria-label={`Delete account ${account.name}`}
                            onClick={(event) => handleDeleteAccountClick(event, account)}
                            disabled={isDeletingAccount}
                            style={{ flexShrink: 0, minWidth: "36px", height: "36px" }}
                          >
                            <TvI className="mdi mdi-delete-outline" />
                          </TvButton>
                        )}
                      </TvDiv>
                    );
                  })}

                  {showAccountSwitcherModal && !isFetchingAccounts && filteredAccounts.length === 0 && (
                    <TvDiv className="text-muted text-center py-3" style={{ fontSize: "13px" }}>
                      No accounts found.
                    </TvDiv>
                  )}
                </TvDiv>

                {isSuperAdmin && (
                  <TvDiv className="border-top p-2" style={{ flexShrink: 0 }}>
                    <TvButton
                      type="button"
                      color="primary" className="w-100"
                      data-testid="header-account-add-button"
                      style={{ borderRadius: "10px", fontWeight: 600 }}
                      onClick={() => {
                        openAddAccountModal();
                      }}
                    >
                      Add New Account
                    </TvButton>
                  </TvDiv>
                )}
                </TvDiv>
                </TvDiv>
              </TvModalRoot>
            </>
            ) : null}
            {!showAccountSwitcher && !allowAccountSwitch ? (
              <TvDiv
                className="ms-2 px-2 py-1 d-flex align-items-center"
                data-testid="header-account-static-badge"
                style={accountChipSurfaceStyle}
              >
                <TvDiv className="me-2">{renderAccountAvatar(currentAccountPreview, 28)}</TvDiv>
                <TvDiv className="d-flex flex-column text-start">
                  <TvSpan style={accountMutedTextStyle}>
                    {props.t("Account")}
                  </TvSpan>
                  <TvSpan style={accountNameTextStyle}>
                    {activeAccountName || "Selected account"}
                  </TvSpan>
                </TvDiv>
              </TvDiv>
            ) : null}
            <TvModalRoot isOpen={showAddAccountModal} toggle={closeAddAccountModal} centered>
              <TvModalHead toggle={closeAddAccountModal}>Add New Account</TvModalHead>
              <TvForm onSubmit={handleCreateAccountFromDialog}>
                <TvModalMain>
                  <TvFormGroup>
                    <TvLabel htmlFor="add-account-name">Account Name</TvLabel>
                    <TvInput
                      id="add-account-name"
                      type="text"
                      value={addAccountForm.name}
                      onChange={(event) => {
                        setAddAccountForm((prev) => ({ ...prev, name: event.target.value }));
                        if (addAccountErrors.name) {
                          setAddAccountErrors((prev) => ({ ...prev, name: "" }));
                        }
                      }}
                      placeholder="Enter account name"
                    />
                    {addAccountErrors.name && <TvSmall className="text-danger">{addAccountErrors.name}</TvSmall>}
                  </TvFormGroup>
                  <TvFormGroup>
                    <TvLabel htmlFor="add-account-logo">Account Logo</TvLabel>
                    <TvInput
                      id="add-account-logo"
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={(event) => {
                        const file = event.target.files?.[0] || null;
                        setAddAccountForm((prev) => ({ ...prev, logo: file }));
                        if (addAccountErrors.logo) {
                          setAddAccountErrors((prev) => ({ ...prev, logo: "" }));
                        }
                      }}
                    />
                    {addAccountErrors.logo && <TvSmall className="text-danger">{addAccountErrors.logo}</TvSmall>}
                  </TvFormGroup>
                  <TvFormGroup tag="fieldset" className="mb-0">
                    <TvLabel className="d-block mb-2">Status</TvLabel>
                    <TvDiv className="d-flex gap-3">
                      <TvLabel className="mb-0 d-flex align-items-center gap-1">
                        <TvRadio
                          name="account-status"
                          checked={addAccountForm.is_active}
                          onChange={() => setAddAccountForm((prev) => ({ ...prev, is_active: true }))}
                        />
                        <TvSpan>Active</TvSpan>
                      </TvLabel>
                      <TvLabel className="mb-0 d-flex align-items-center gap-1">
                        <TvRadio
                          name="account-status"
                          checked={!addAccountForm.is_active}
                          onChange={() => setAddAccountForm((prev) => ({ ...prev, is_active: false }))}
                        />
                        <TvSpan>Inactive</TvSpan>
                      </TvLabel>
                    </TvDiv>
                  </TvFormGroup>
                </TvModalMain>
                <TvModalFoot>
                  <TvButton type="button" color="light" onClick={closeAddAccountModal} disabled={creatingAccount}>
                    Cancel
                  </TvButton>
                  <TvButton type="submit" color="primary" disabled={creatingAccount}>
                    {creatingAccount ? "Creating..." : "Create & Switch"}
                  </TvButton>
                </TvModalFoot>
              </TvForm>
            </TvModalRoot>
            <DeleteModal
              show={isDeleteModalOpen}
              onDeleteClick={handleDeleteAccountConfirm}
              onCloseClick={() => {
                if (isDeletingAccount) {
                  return;
                }
                setIsDeleteModalOpen(false);
                setAccountToDelete(null);
              }}
              message={
                accountToDelete?.name
                  ? `Are you sure you want to delete account "${accountToDelete.name}"?`
                  : "Are you sure you want to delete this account?"
              }
            />
          </TvDiv>
          <TvDiv className="d-flex">
           {/* <TvDiv className="dropdown d-inline-block d-lg-none ms-2">
              <TvButton
                onClick={() => {
                  setsearch(!search);
                }}
                type="button"
                className="btn header-item noti-icon "
                id="page-header-search-dropdown"
              >
                <TvI className="mdi mdi-magnify" />
              </TvButton>
              <TvDiv
                className={
                  search
                    ? "dropdown-menu dropdown-menu-lg dropdown-menu-end p-0 show"
                    : "dropdown-menu dropdown-menu-lg dropdown-menu-end p-0"
                }
                aria-labelledby="page-header-search-dropdown"
              >
                <TvForm className="p-3">
                  <TvDiv className="form-group m-0">
                    <TvDiv className="input-group">
                      <TvInput
                        type="text"
                        className="form-control"
                        placeholder="Search ..."
                        aria-label="Recipient's username"
                      />
                      <TvDiv className="input-group-append">
                        <TvButton className="btn btn-primary" type="submit">
                          <TvI className="mdi mdi-magnify" />
                        </TvButton>
                      </TvDiv>
                    </TvDiv>
                  </TvDiv>
                </TvForm>
              </TvDiv>
            </TvDiv>*/}
            <TvDiv className="tv-saas-toputil" data-testid="header-saas-toputil">
              <Link
                to={
                  activeAccountId
                    ? toScopedRoute(activeAccountId, ROUTE_PATHS.billing)
                    : ROUTE_PATHS.billing
                }
                className="tv-saas-credit-pill"
                data-testid="header-saas-credit-pill"
                title="Billing & credits"
              >
                {headerCreditPillText}
              </Link>
              <TvSpan
                className={`tv-saas-plan-badge${isPremiumPlan ? " is-premium" : " is-free"}`}
                data-testid="header-saas-plan-badge"
              >
                {planBadgeLabel}
              </TvSpan>
              <TvButton
                type="button"
                color="primary"
                size="sm"
                className="tv-saas-create-btn"
                data-testid="header-saas-create"
                onClick={openCreateDesign}
              >
                + Create
              </TvButton>
              <TvButton
                tag={Link}
                to={
                  activeAccountId
                    ? toScopedRoute(activeAccountId, ROUTE_PATHS.buyCredits)
                    : ROUTE_PATHS.buyCredits
                }
                color="warning"
                size="sm"
                className="tv-saas-upgrade-btn"
                data-testid="header-saas-upgrade"
              >
                {isPremiumPlan || isEnterprisePlan ? "Top up" : "Upgrade"}
              </TvButton>
            </TvDiv>
            <ProfileMenu />
          </TvDiv>
        </TvDiv>
      </TvDiv>
    </React.Fragment>
  );
};

Header.propTypes = {
  changeSidebarType: PropTypes.func,
  hideLeftSidebar: PropTypes.bool,
  leftMenu: PropTypes.any,
  leftSideBarType: PropTypes.any,
  showRightSidebar: PropTypes.any,
  showRightSidebarAction: PropTypes.func,
  t: PropTypes.any,
  toggleLeftmenu: PropTypes.func,
};

const mapStatetoProps = state => {
  const {
    layoutType,
    showRightSidebar,
    leftMenu,
    leftSideBarType,
  } = state.Layout;
  return { layoutType, showRightSidebar, leftMenu, leftSideBarType };
};

export default connect(mapStatetoProps, {
  showRightSidebarAction,
  toggleLeftmenu,
  changeSidebarType,
})(withTranslation()(Header));
